import classNames from 'classnames'
import _ from 'lodash'
import IMask from 'imask'
import React from 'react'

import { IBaseProps } from 'browser/components/atomic-elements/atoms/base-props'
import { FramesManager } from 'shared-libs/components/view/frames-manager'
import { translateString } from 'shared-libs/helpers/utils'
import { UI2FieldControl } from '../field-ui/field-control/field-control'
import { IMASK_DEFINITIONS, MaskStoredAs, parseMaskToImask } from './ui2-input-mask'

import {
  applyTextMask,
  getDefaultInputPlaceholder,
  type UI2InputFieldProps,
} from 'shared-libs/components/ui2/fields/ui2-input-field/ui2-input-field-props'
import { UI2FieldShell, IUI2FieldShellProps } from '../field-ui/field-shell/field-shell'
import { UI2NoValueAvailable } from '../field-ui/no-value-available/no-value-available'
import { IFieldShellReadOnlyHelpers } from 'shared-libs/components/ui2/fields/field-ui/field-shell/field-shell-props'

export { MaskStoredAs }
export type AutoCapitalize = 'none' | 'characters' | 'words'

// DW-specific extras not in the cross-platform UI2InputFieldProps.
interface IUI2InputFieldWebExtras extends Omit<IBaseProps, 'children'> {
  onChange?: (value: any) => void
  frames?: FramesManager
  translationTable?: any
  autoCapitalize?: AutoCapitalize
  type?: string
  inputMode?: string
  maxLength?: number
  autoFocus?: boolean
  autoComplete?: string
  rightAddon?: React.ReactNode
  isLoading?: boolean
}

/**
 * @uiComponent
 */
export interface IUI2InputFieldProps extends UI2InputFieldProps, IUI2InputFieldWebExtras {}

function applyCapitalization(value: string, mode: AutoCapitalize): string {
  if (mode === 'characters') return value.toUpperCase()
  if (mode === 'words') return value.replace(/\b\w/g, (ch) => ch.toUpperCase())
  return value
}

interface IUI2InputFieldState {
  unmaskedDisplayValue: string
}

export class UI2InputField extends React.Component<IUI2InputFieldProps, IUI2InputFieldState> {
  private inputRef = React.createRef<HTMLInputElement>()
  private maskInstance: IMask.InputMask<IMask.MaskedPatternOptions> | null = null

  constructor(props: IUI2InputFieldProps) {
    super(props)
    this.state = {
      unmaskedDisplayValue: this.formatDisplay(props.value, props.autoCapitalize),
    }
  }

  public componentDidMount() {
    if (this.props.textMask) this.initMask()
  }

  public componentDidUpdate(prevProps: IUI2InputFieldProps) {
    const { textMask, value, maskStoredAs = 'raw', autoCapitalize } = this.props
    if (textMask !== prevProps.textMask || autoCapitalize !== prevProps.autoCapitalize) {
      this.destroyMask()
      if (textMask) this.initMask()
      if (!textMask) this.setState({ unmaskedDisplayValue: this.formatDisplay(value, autoCapitalize) })
      return
    }
    if (!textMask && value !== prevProps.value) {
      const formatted = this.formatDisplay(value, autoCapitalize)
      if (formatted !== this.state.unmaskedDisplayValue) {
        this.setState({ unmaskedDisplayValue: formatted })
      }
    }
    if (this.maskInstance && value !== prevProps.value) {
      if (maskStoredAs === 'raw') {
        this.maskInstance.unmaskedValue = _.isNil(value) ? '' : String(value)
      } else {
        this.maskInstance.value = _.isNil(value) ? '' : String(value)
      }
    }
  }

  public componentWillUnmount() {
    this.destroyMask()
  }

  public render() {
    const { className, rightAddon } = this.props
    return (
      <UI2FieldShell
        {...(this.props as unknown as IUI2FieldShellProps)}
        className={classNames('ui2-inputField', className)}
        bodyIsFieldControl
        renderReadOnly={this.renderReadOnlyValue}
      >
        <UI2FieldControl rightAddon={rightAddon}>
          {this.renderInput()}
        </UI2FieldControl>
      </UI2FieldShell>
    )
  }

  private renderReadOnlyValue = (value: string | undefined, { editNode }: IFieldShellReadOnlyHelpers): React.ReactNode => {
    const { textMask, maskStoredAs = 'raw' } = this.props
    let content: React.ReactNode
    if (_.isNil(value) || value === '') {
      content = <UI2NoValueAvailable />
    } else {
      const raw = String(value)
      content = (!textMask || maskStoredAs === 'formatted')
        ? <span>{raw}</span>
        : <span>{applyTextMask(textMask, raw) ?? raw}</span>
    }
    return (
      <>
        <div className='ui2-fieldShell-readOnly-content'>{content}</div>
        {editNode}
      </>
    )
  }

  private renderInput(): React.ReactNode {
    const {
      textMask, value, autoCapitalize, type, inputMode, maxLength,
      autoFocus, autoComplete, placeholder, label, isDisabled, isLoading,
      testID, accessibilityLabel, translationTable,
    } = this.props
    const translatedLabel = translateString(label, translationTable)
    const translatedPlaceholder = placeholder
      ? translateString(placeholder, translationTable)
      : undefined
    const resolvedPlaceholder = translatedPlaceholder ?? getDefaultInputPlaceholder(translatedLabel)
    const isMasked = !!textMask
    const inputValue = isMasked ? undefined : this.state.unmaskedDisplayValue
    return (
      <input
        ref={this.inputRef}
        type={type ?? 'text'}
        value={inputValue}
        defaultValue={isMasked ? '' : undefined}
        onChange={isMasked ? undefined : this.handleChange}
        placeholder={resolvedPlaceholder}
        disabled={isDisabled || isLoading}
        inputMode={inputMode as React.HTMLAttributes<HTMLInputElement>['inputMode']}
        maxLength={maxLength}
        autoFocus={autoFocus}
        autoComplete={autoComplete}
        data-testid={testID}
        aria-label={accessibilityLabel}
      />
    )
  }

  private handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const raw = event.target.value
    const { autoCapitalize, onChange } = this.props
    const next = autoCapitalize && autoCapitalize !== 'none'
      ? applyCapitalization(raw, autoCapitalize)
      : raw
    this.setState({ unmaskedDisplayValue: next })
    onChange?.(next)
  }

  private formatDisplay(value: any, autoCapitalize?: AutoCapitalize): string {
    if (_.isNil(value)) return ''
    const str = String(value)
    if (!autoCapitalize || autoCapitalize === 'none') return str
    return applyCapitalization(str, autoCapitalize)
  }

  private initMask() {
    const { textMask, value, maskStoredAs = 'raw', autoCapitalize } = this.props
    if (!textMask || !this.inputRef.current) return
    const pattern = parseMaskToImask(textMask)
    const prepare = autoCapitalize === 'characters'
      ? (str: string) => str.toUpperCase()
      : autoCapitalize === 'words'
        ? (str: string, masked: IMask.MaskedPattern) => {
            const isWordStart = masked.value.length === 0 || /\s$/.test(masked.value)
            return isWordStart ? str.toUpperCase() : str
          }
        : undefined
    this.maskInstance = IMask(this.inputRef.current, {
      mask: pattern,
      definitions: IMASK_DEFINITIONS,
      ...(prepare && { prepare }),
    })
    if (!_.isNil(value)) {
      if (maskStoredAs === 'raw') this.maskInstance.unmaskedValue = String(value)
      else this.maskInstance.value = String(value)
    }
    this.maskInstance.on('accept', this.handleMaskAccept)
  }

  private destroyMask() {
    if (this.maskInstance) {
      this.maskInstance.off('accept', this.handleMaskAccept)
      this.maskInstance.destroy()
      this.maskInstance = null
    }
  }

  private handleMaskAccept = () => {
    const { onChange, maskStoredAs = 'raw' } = this.props
    if (!onChange || !this.maskInstance) return
    onChange(maskStoredAs === 'raw' ? this.maskInstance.unmaskedValue : this.maskInstance.value)
  }
}
